import {HierarchicalOrganizationalUnit} from '../../admin/artisticobjects/types/OrganizationalUnit';
import {intersection} from 'lodash-es';

export class OrganizationalUnitsSelectionState {
  private readonly unitsById: Map<string, HierarchicalOrganizationalUnit>;
  private readonly unitsByLevel: Map<number, HierarchicalOrganizationalUnit[]>;
  private maxLevel: number;
  private selectedByLevel: Record<number, string[]>;
  private availableByLevel: Record<number, string[]>;

  constructor(allUnits: HierarchicalOrganizationalUnit[]) {
    this.unitsById = new Map();
    this.unitsByLevel = new Map();
    this.maxLevel = 0;
    this.selectedByLevel = {};
    this.availableByLevel = {};

    this.buildIndexes(allUnits);
  }

  private buildIndexes(allUnits: HierarchicalOrganizationalUnit[]): void {
    if (allUnits.length === 0) {
      return;
    }

    // Calculate max level
    this.maxLevel = Math.max(...allUnits.map(u => u.level));

    // Initialize unitsById and unitsByLevel
    allUnits.forEach(unit => {
      this.unitsById.set(unit.id, unit);

      if (!this.unitsByLevel.has(unit.level)) {
        this.unitsByLevel.set(unit.level, []);
      }
      this.unitsByLevel.get(unit.level)!.push(unit);
    });

    // Initialize availableByLevel with all units by level
    for (let level = 0; level <= this.maxLevel; level++) {
      const unitsAtLevel = this.unitsByLevel.get(level) || [];
      this.availableByLevel[level] = unitsAtLevel.map(u => u.id);
    }
  }

  getUnitById(id: string): HierarchicalOrganizationalUnit | undefined {
    return this.unitsById.get(id);
  }

  getUnitsByLevel(level: number): HierarchicalOrganizationalUnit[] {
    return this.unitsByLevel.get(level) || [];
  }

  getMaxLevel(): number {
    return this.maxLevel;
  }

  setSelectedFlat(selectedIds: string[]): void {
    const selectedByLevel: Record<number, string[]> = {};

    selectedIds.forEach(id => {
      const unit = this.unitsById.get(id);
      if (unit) {
        if (!selectedByLevel[unit.level]) {
          selectedByLevel[unit.level] = [];
        }
        selectedByLevel[unit.level].push(id);
      }
    });

    this.setSelected(selectedByLevel);
  }

  private setSelected(selectedByLevel: Record<number, string[]>): void {
    this.selectedByLevel = {};

    for (let level = 0; level <= this.maxLevel; level++) {
      // Calculate available units at level
      if (level === 0) {
        // Level 0: available is already initialized in buildIndexes
        // No need to recalculate
      } else {
        // Get effective parent IDs from previous level
        const effectiveParentIds = this.getEffectiveByLevel(level - 1);

        // Calculate available units: children of effective parents
        const unitsAtLevel = this.unitsByLevel.get(level) || [];
        this.availableByLevel[level] = unitsAtLevel
          .filter(unit => effectiveParentIds.includes(unit.idsPath[level - 1]))
          .map(u => u.id);
      }

      // Calculate selected at level: intersection of requested selection and available
      const requestedSelection = selectedByLevel[level] || [];
      const availableAtLevel = this.availableByLevel[level] || [];
      const validSelection = intersection(requestedSelection, availableAtLevel);

      // Store selection, but treat "select all" as "no filter" (empty selection)
      if (validSelection.length === availableAtLevel.length) {
        delete this.selectedByLevel[level];
      } else {
        this.selectedByLevel[level] = validSelection;
      }
    }
    // console.log("selectedByLevel", this.selectedByLevel);
    // console.log("availableByLevel", this.availableByLevel);
  }

  private getEffectiveByLevel(level: number): string[] {
    const selected = this.selectedByLevel[level];
    const available = this.availableByLevel[level] || [];

    return selected && selected.length > 0 ? selected : available;
  }

  getSelectedByLevel(level: number): string[] {
    return this.selectedByLevel[level] || [];
  }

  getAvailableByLevel(level: number): string[] {
    return this.availableByLevel[level] || [];
  }

  getAvailableUnitsByLevel(level: number): HierarchicalOrganizationalUnit[] {
    const availableIds = this.availableByLevel[level] || [];
    return availableIds
      .map(id => this.unitsById.get(id))
      .filter((unit): unit is HierarchicalOrganizationalUnit => unit !== undefined);
  }

  getResolvedSelection(): string[] {
    // Find the first level with a selection
    let firstSelectionLevel = this.getFirstSelectionLevel();

    const result: string[] = [];

    // If no selection at any level, return all effective items from all levels
    for (let level = firstSelectionLevel < 0 ? 0 : firstSelectionLevel; level <= this.maxLevel; level++) {
      result.push(...this.getEffectiveByLevel(level));
    }

    // console.log("resolvedSelection", result);
    return result;
  }

  private getFirstSelectionLevel() {
    let firstSelectionLevel = -1;
    for (let level = 0; level <= this.maxLevel; level++) {
      if (this.selectedByLevel[level] && this.selectedByLevel[level].length > 0) {
        firstSelectionLevel = level;
        break;
      }
    }
    return firstSelectionLevel;
  }

  setSelectedAtLevel(level: number, selectedIds: string[]): void {
    const updated = { ...this.selectedByLevel, [level]: selectedIds };
    this.setSelected(updated);
  }
}

